import { Logo, styles as orbitStyles, useLayout } from "@withorbit/ui";
import React, { useEffect, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { router } from "expo-router";
import { NavigationBar } from "../../components/NavigationBar.js";
import { useDatabaseManager } from "../../model2/databaseManagerContext.js";
import { AuthAwareSignInButton } from "../../infoPage/AuthAwareSignInButton.js";

const { layout } = orbitStyles;

// ============================================================================
// Andy Matuschak's Orbit Design System
// ============================================================================
// These colors come directly from Andy's demo project CSS variables.
// The aesthetic is warm, cream-based, with red accents and brown-tinted shadows.

const orbit = {
  // Backgrounds - warm cream palette
  bgPrimary: "#F9F6F1",
  bgSecondary: "#eeeae2",
  bgContent: "#fdfcfb",

  // Accents - red primary, amber secondary
  accentPrimary: "#D60909",
  accentSecondary: "#A06813",

  // Text hierarchy - warm grays/browns
  fgPrimary: "rgba(0,0,0,0.8)",
  fgSecondaryLarge: "#908672",
  fgSecondarySmall: "#826D4E",
  fgTertiary: "#E2DCD2", // border color
  fgDisabled: "#AAAAAA",

  // Interactive states - warm brown overlays
  hoverLayer: "rgba(92, 56, 0, 0.05)",
  pressedLayer: "rgba(92, 56, 0, 0.14)",
  selection: "#F9EBD9",
  selectionHover: "#D6090926",

  // Shadows - always brown-tinted, never pure black
  shadowLight: "0px 4px 12px rgba(77, 51, 8, 0.08)",
  shadowMedium: "0px 7px 20px rgba(77, 51, 8, 0.09), 0px 4px 8px rgba(77, 51, 8, 0.11)",
  shadowHeavy: "0px 8px 24px rgba(77, 51, 8, 0.12), 0px 4px 12px rgba(77, 51, 8, 0.08)",
};

// ============================================================================
// Styles
// ============================================================================

const componentStyles = StyleSheet.create({
  // === Layout ===
  container: {
    flex: 1,
    backgroundColor: orbit.bgSecondary,
    width: "100%",
  },
  scrollContent: {
    padding: layout.gridUnit * 4,
    maxWidth: 560,
    alignSelf: "center",
    width: "100%",
    paddingBottom: layout.gridUnit * 8,
  },

  // === Header ===
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: layout.gridUnit * 3,
  },

  // === Hero Section - Due Today CTA ===
  heroCard: {
    backgroundColor: orbit.bgPrimary,
    borderRadius: 8,
    borderWidth: 3,
    borderColor: orbit.fgTertiary,
    padding: layout.gridUnit * 3,
    marginBottom: layout.gridUnit * 3,
    // Orbit shadow
    shadowColor: "rgba(77, 51, 8, 0.12)",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 6,
  },
  heroLabel: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 11,
    letterSpacing: 0.06,
    textTransform: "uppercase",
    color: orbit.fgSecondarySmall,
    marginBottom: layout.gridUnit,
  },
  heroNumber: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 64,
    lineHeight: 64,
    letterSpacing: -2,
    color: orbit.accentPrimary,
    marginBottom: layout.gridUnit * 0.5,
  },
  heroNumberMuted: {
    color: orbit.fgSecondaryLarge,
  },
  heroSubtext: {
    fontFamily: "Dr-Medium, system-ui, sans-serif",
    fontSize: 17,
    lineHeight: 22,
    color: orbit.fgPrimary,
    marginBottom: layout.gridUnit * 2.5,
  },
  reviewButton: {
    backgroundColor: orbit.accentPrimary,
    borderRadius: 4,
    paddingVertical: layout.gridUnit * 1.75,
    paddingHorizontal: layout.gridUnit * 3,
    alignItems: "center",
    alignSelf: "flex-start",
  },
  reviewButtonDisabled: {
    backgroundColor: orbit.fgSecondaryLarge,
    opacity: 0.5,
  },
  reviewButtonText: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 15,
    letterSpacing: 0.02,
    color: "#FFFFFF",
  },

  // === Stats Row ===
  statsRow: {
    flexDirection: "row",
    gap: layout.gridUnit * 2,
    marginBottom: layout.gridUnit * 4,
  },
  statCard: {
    flex: 1,
    backgroundColor: orbit.bgPrimary,
    borderRadius: 6,
    borderWidth: 3,
    borderColor: orbit.fgTertiary,
    padding: layout.gridUnit * 2,
    alignItems: "center",
  },
  statValue: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 32,
    lineHeight: 32,
    letterSpacing: -1,
    color: orbit.fgPrimary,
    marginBottom: layout.gridUnit * 0.5,
  },
  statLabel: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 10,
    letterSpacing: 0.04,
    textTransform: "uppercase",
    color: orbit.fgSecondarySmall,
  },

  // === Section ===
  section: {
    marginBottom: layout.gridUnit * 4,
  },
  sectionTitle: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 13,
    letterSpacing: 0.04,
    textTransform: "uppercase",
    color: orbit.fgSecondarySmall,
    marginBottom: layout.gridUnit * 1.5,
  },

  // === Menu Items (Andy-style) ===
  menuItem: {
    backgroundColor: orbit.bgPrimary,
    borderWidth: 3,
    borderColor: orbit.fgTertiary,
    borderRadius: 0,
    paddingVertical: layout.gridUnit * 1.5,
    paddingHorizontal: layout.gridUnit * 2,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    // First item gets top radius
    marginTop: -3,
  },
  menuItemFirst: {
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    marginTop: 0,
  },
  menuItemLast: {
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  menuItemContent: {
    flex: 1,
  },
  menuItemTitle: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 17,
    lineHeight: 20,
    letterSpacing: 0.01,
    color: orbit.accentPrimary,
  },
  menuItemSubtitle: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 13,
    lineHeight: 16,
    letterSpacing: 0.02,
    color: orbit.fgSecondaryLarge,
    marginTop: layout.gridUnit * 0.5,
  },
  menuItemArrow: {
    fontFamily: "Dr-Bold, system-ui, sans-serif",
    fontSize: 18,
    color: orbit.fgSecondaryLarge,
    marginLeft: layout.gridUnit,
  },

  // === Large Layout ===
  largeContainer: {
    width: "100%",
    minHeight: "100%",
  },
  largeHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: layout.gridUnit * 4,
    paddingHorizontal: layout.gridUnit * 6,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
    marginBottom: layout.gridUnit * 2,
  },
  largeNavRow: {
    alignItems: "center",
    marginBottom: layout.gridUnit * 4,
  },
  largeContentRow: {
    flexDirection: "row",
    maxWidth: 1200,
    width: "100%",
    alignSelf: "center",
    paddingHorizontal: layout.gridUnit * 6,
    justifyContent: "center",
    gap: layout.gridUnit * 8,
  },
  largeMainColumn: {
    width: 480,
    flexShrink: 0,
    paddingBottom: layout.gridUnit * 8,
  },
  largeLearnMoreColumn: {
    flex: 1,
    paddingBottom: layout.gridUnit * 8,
    maxWidth: 560,
  },

  // === Learn More Content ===
  learnMoreHeading: {
    fontFamily: "Dr-Bold, system-ui, sans-serif",
    fontSize: 32,
    lineHeight: 38,
    letterSpacing: -0.5,
    color: orbit.fgPrimary,
    marginBottom: layout.gridUnit * 2,
  },
  learnMoreHeadingFirst: {
    marginTop: 0,
  },
  learnMoreHeadingSubsequent: {
    marginTop: layout.gridUnit * 6,
  },
  learnMoreText: {
    fontFamily: "Dr-Medium, system-ui, sans-serif",
    fontSize: 17,
    lineHeight: 26,
    letterSpacing: 0.01,
    color: orbit.fgPrimary,
    marginBottom: layout.gridUnit * 2.5,
  },
});

// ============================================================================
// Components
// ============================================================================

interface MenuItemProps {
  title: string;
  subtitle?: string;
  onPress: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

function MenuItem({ title, subtitle, onPress, isFirst, isLast }: MenuItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      style={[
        componentStyles.menuItem,
        isFirst && componentStyles.menuItemFirst,
        isLast && componentStyles.menuItemLast,
        isHovered && { backgroundColor: orbit.hoverLayer },
      ]}
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <View style={componentStyles.menuItemContent}>
        <Text style={componentStyles.menuItemTitle}>{title}</Text>
        {subtitle && <Text style={componentStyles.menuItemSubtitle}>{subtitle}</Text>}
      </View>
      <Text style={componentStyles.menuItemArrow}>→</Text>
    </Pressable>
  );
}

function HeroCard({ stats }: { stats: { dueToday: number; totalCards: number } | null }) {
  const hasDue = (stats?.dueToday ?? 0) > 0;

  return (
    <View style={componentStyles.heroCard}>
      <Text style={componentStyles.heroLabel}>Ready to review</Text>
      <Text style={[
        componentStyles.heroNumber,
        !hasDue && componentStyles.heroNumberMuted,
      ]}>
        {stats?.dueToday ?? "—"}
      </Text>
      <Text style={componentStyles.heroSubtext}>
        {hasDue
          ? `prompt${stats!.dueToday !== 1 ? "s" : ""} due today`
          : "No prompts due right now"}
      </Text>
      <Pressable
        style={[
          componentStyles.reviewButton,
          !hasDue && componentStyles.reviewButtonDisabled,
        ]}
        onPress={() => router.push("/review")}
        disabled={!hasDue}
      >
        <Text style={componentStyles.reviewButtonText}>
          {hasDue ? "Start Review" : "All caught up"}
        </Text>
      </Pressable>
    </View>
  );
}

function StatsRow({ stats }: { stats: { dueToday: number; totalCards: number } | null }) {
  const learned = stats ? Math.round(stats.totalCards * 0.7) : null;

  return (
    <View style={componentStyles.statsRow}>
      <View style={componentStyles.statCard}>
        <Text style={componentStyles.statValue}>{stats?.totalCards ?? "—"}</Text>
        <Text style={componentStyles.statLabel}>Total</Text>
      </View>
      <View style={componentStyles.statCard}>
        <Text style={componentStyles.statValue}>{learned ?? "—"}</Text>
        <Text style={componentStyles.statLabel}>Learned</Text>
      </View>
    </View>
  );
}

function LearnMoreContent() {
  return (
    <View>
      <Text style={[componentStyles.learnMoreHeading, componentStyles.learnMoreHeadingFirst]}>
        Orbit makes memory a choice
      </Text>
      <Text style={componentStyles.learnMoreText}>
        It's frustrating how quickly we forget much of what we read. We may not care when we're just reading for fun. But it's hard to make sense of complex ideas when they build on earlier details we've already begun to forget. Often we take away a vague gist instead of accurate understanding.
      </Text>
      <Text style={componentStyles.learnMoreText}>
        Happily, cognitive scientists have developed simple strategies which can ensure you'll remember something permanently. Orbit lets authors build those techniques into their writing, making it easy for you to remember what you've read.
      </Text>
      <Text style={componentStyles.learnMoreText}>
        As you read, Orbit occasionally prompts you with quick questions to reinforce the ideas you've just seen. Memory fades, so after a few days, Orbit will send you an email inviting you to quickly review the text's questions. If you still remember an answer, you'll next see it two weeks later. Then a month after that, then two months, and so on. If you forget an answer, the schedule contracts to reinforce that question.
      </Text>
      <Text style={componentStyles.learnMoreText}>
        The expanding schedule means that you need just a few reviews (usually a few seconds each) to remember an answer for years. Memory is typically haphazard—we read something interesting and hope we'll remember it—but this review system makes memory a choice.
      </Text>

      <Text style={[componentStyles.learnMoreHeading, { marginTop: layout.gridUnit * 3 }]}>
        Bringing ideas into your orbit
      </Text>
      <Text style={componentStyles.learnMoreText}>
        You may have made flashcards in school to memorize facts like vocabulary words or anatomy. When used well, simple flashcards can automate away learning the "easy part" of a subject so you can focus on deeper, more conceptual issues. But one of the ideas motivating Orbit is that these review techniques can also directly help people master abstract, conceptual knowledge.
      </Text>
      <Text style={componentStyles.learnMoreText}>
        Orbit questions can go beyond facts to probe connections, implications, causes, and consequences. They can prompt you to reflect or to synthesize something new. And by weaving these questions into a narrative, authors can situate them in a rich context, breaking the ordinary constraints of flashcards as a medium.
      </Text>
      <Text style={componentStyles.learnMoreText}>
        When you read a text that's written with Orbit, you don't just read it once and then set it aside, perhaps forever. The review sessions keep you in contact with the ideas, returning you to the material again and again over weeks and months. The ongoing practice changes your relationship to what you read. It gives you a way to bring ideas into your orbit.
      </Text>
    </View>
  );
}

// Content for both layouts
function MainContent({ stats }: { stats: { dueToday: number; totalCards: number } | null }) {
  return (
    <>
      {/* Hero: Due Today */}
      <HeroCard stats={stats} />

      {/* Stats */}
      <StatsRow stats={stats} />

      {/* Add Prompts */}
      <View style={componentStyles.section}>
        <Text style={componentStyles.sectionTitle}>Add prompts</Text>
        <MenuItem
          title="Create Prompt"
          subtitle="Write a question and answer"
          onPress={() => router.push("/create")}
          isFirst
        />
        <MenuItem
          title="Import from File"
          subtitle="TSV, CSV, or Anki .apkg"
          onPress={() => router.push("/import")}
          isLast={Platform.OS !== "web"}
        />
        {Platform.OS === "web" && (
          <MenuItem
            title="Read PDF"
            subtitle="Add prompts as you read"
            onPress={() => router.push("/pdf")}
            isLast
          />
        )}
      </View>

      {/* Manage */}
      <View style={componentStyles.section}>
        <Text style={componentStyles.sectionTitle}>Manage</Text>
        <MenuItem
          title="Collections"
          subtitle="Browse and organize prompts"
          onPress={() => router.push("/collections")}
          isFirst
          isLast
        />
      </View>

      {/* Account */}
      <View style={componentStyles.section}>
        <Text style={componentStyles.sectionTitle}>Account</Text>
        <MenuItem
          title="Settings"
          subtitle="API access and preferences"
          onPress={() => router.push("/settings?action=generatePersonalAccessToken")}
          isFirst
          isLast
        />
      </View>
    </>
  );
}

// ============================================================================
// Layouts
// ============================================================================

// Hide scrollbar styles for web
const hideScrollbarStyle = Platform.OS === "web" ? {
  // @ts-ignore - web-specific CSS
  scrollbarWidth: "none",
  msOverflowStyle: "none",
} : {};

function SmallLayout({ stats }: { stats: { dueToday: number; totalCards: number } | null }) {
  return (
    <ScrollView
      style={[{ flex: 1, backgroundColor: orbit.bgSecondary }, hideScrollbarStyle]}
      contentContainerStyle={componentStyles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={componentStyles.headerRow}>
        <a href="https://withorbit.com">
          <Logo units={4} tintColor={orbit.fgPrimary} />
        </a>
        <AuthAwareSignInButton color={orbit.fgPrimary} />
      </View>

      <View style={{ marginBottom: layout.gridUnit * 3 }}>
        <NavigationBar currentRoute="home" />
      </View>

      <MainContent stats={stats} />

      <View style={{ marginTop: layout.gridUnit * 4 }}>
        <LearnMoreContent />
      </View>
    </ScrollView>
  );
}

function LargeLayout({ stats }: { stats: { dueToday: number; totalCards: number } | null }) {
  return (
    <ScrollView style={[{ flex: 1, backgroundColor: orbit.bgSecondary }, hideScrollbarStyle]} showsVerticalScrollIndicator={false}>
      <View style={componentStyles.largeContainer}>
        {/* Header */}
        <View style={componentStyles.largeHeaderRow}>
          <a href="https://withorbit.com">
            <Logo units={4} tintColor={orbit.fgPrimary} />
          </a>
          <AuthAwareSignInButton color={orbit.fgPrimary} />
        </View>

        {/* Navigation */}
        <View style={componentStyles.largeNavRow}>
          <NavigationBar currentRoute="home" />
        </View>

        {/* Two Column Content */}
        <View style={componentStyles.largeContentRow}>
          {/* Main Column */}
          <View style={componentStyles.largeMainColumn}>
            <MainContent stats={stats} />
          </View>

          {/* Learn More Column */}
          <View style={componentStyles.largeLearnMoreColumn}>
            <LearnMoreContent />
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function LibraryScreen() {
  const databaseManager = useDatabaseManager();
  const { width, onLayout } = useLayout();
  const [stats, setStats] = useState<{
    dueToday: number;
    totalCards: number;
  } | null>(null);

  // Hide scrollbar on web (webkit browsers)
  useEffect(() => {
    if (Platform.OS === "web") {
      const style = document.createElement("style");
      style.id = "library-hide-scrollbar";
      style.textContent = `
        *::-webkit-scrollbar { display: none; }
        * { -ms-overflow-style: none; scrollbar-width: none; }
      `;
      document.head.appendChild(style);
      return () => {
        const el = document.getElementById("library-hide-scrollbar");
        if (el) el.remove();
      };
    }
  }, []);

  useEffect(() => {
    async function loadStats() {
      if (!databaseManager) return;
      try {
        const collections = await databaseManager.listCollections();
        const totalCards = collections.reduce((sum, c) => sum + c.totalPromptCount, 0);
        const dueToday = collections.reduce((sum, c) => sum + c.duePromptCount, 0);
        setStats({ dueToday, totalCards });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes("Database has been closed") || errorMessage.includes("DatabaseClosed")) {
          console.log("[Library] Database closed during load - likely navigated away");
          return;
        }
        console.error("Failed to load stats:", error);
      }
    }
    loadStats();
  }, [databaseManager]);

  const isLarge = width >= 1000;

  return (
    <View style={{ flex: 1, backgroundColor: orbit.bgSecondary }} onLayout={onLayout}>
      {width > 0 && (isLarge ? <LargeLayout stats={stats} /> : <SmallLayout stats={stats} />)}
    </View>
  );
}
